import DOMPurify, { Config } from 'dompurify';

const sanitize = DOMPurify.sanitize;

const config = {
  ADD_TAGS: ['iframe', 'img', 'video', 'source'],
  ADD_ATTR: [
    'target',
    'href',
    'src',
    'alt',
    'title',
    'width',
    'height',
    'controls',
    'autoplay',
    'loop',
    'muted',
    'poster'
  ],
  ADD_DATA_URI_TAGS: ['img', 'audio', 'video', 'source'],
  ADD_URI_SAFE_ATTR: ['href', 'src', 'poster'],
  RETURN_TEXT_NODE: true
};

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    const href = node.getAttribute('href');
    if (href && href.includes('javascript:')) {
      node.removeAttribute('href');
    }
  }

  if (node.tagName === 'A' && node.hasAttribute('target') && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }

  const booleanAttributes = ['controls', 'autoplay', 'loop', 'muted'];
  booleanAttributes.forEach(attr => {
    if (node.hasAttribute(attr) && node.getAttribute(attr) === '') {
      node.setAttribute(attr, 'true');
    }
  });
});

export const sanitizeSVG = (svgString: string, options: Config): ReturnType<typeof sanitize> => {
  return sanitize(svgString, options);
};

export const sanitizeJSON = (json: JSON, excludeKeys: string[] = []) => {
  try {
    const sanitizedJSON = JSON.parse(JSON.stringify(json), (key, value) => {
      if (typeof value === 'string' && !excludeKeys.includes(key)) {
        return sanitize(value, config);
      }
      return value;
    });
    return sanitizedJSON;
  } catch (error) {
    console.error('Error sanitizing JSON', error);
    return json;
  }
};

const sanitizeStringWithScriptContent = (string: string) => {
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = string;

    const scripts = tempDiv.querySelectorAll('script');
    let extractedText = '';

    scripts.forEach(script => {
      extractedText += script.textContent || '';
    });

    scripts.forEach(script => script.remove());
    const remainingContent = tempDiv.innerHTML;

    const sanitizedContent = sanitize(remainingContent, config);

    return sanitizedContent + extractedText;
  } catch (error) {
    console.error('Error sanitizing string with script content', error);
    return string;
  }
};

/**
 * Sanitizes a string using DOMPurify.
 * Note: Script tags are removed for security reasons, which may result in empty strings
 * if the input contains only script content.
 *
 * @param string - The string to sanitize
 * @returns The sanitized string (may be empty if input was only script tags)
 */
export const sanitizeString = (string: string) => {
  try {
    const sanitizedString = sanitize(string, config);

    if (sanitizedString.length === 0) {
      return sanitizeStringWithScriptContent(string);
    }

    return sanitizedString;
  } catch (error) {
    console.error('Error sanitizing string', error);
    return string;
  }
};
